import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  AssignmentLateRounded,
  HowToRegRounded,
  ChevronRightRounded,
  EventNoteRounded,
  PendingActionsRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import { assignmentApi, type AssignmentSummaryDto } from '../../../lib/api/assignment.api';
import { programEnrollmentApi } from '../../../lib/api/program-enrollment.api';
import type { SectionWorkDetail, WorkItem } from '../../../lib/api/program-enrollment.api';
import type { SectionStructureDto } from '../../../lib/api/lms.api';
import { errorText } from '../../../lib/error-text';
import { CoursePerformance } from './CoursePerformance';
import { loadAlerts } from './course-summary.alerts';
import { useTenantStore, useTenantTimeZone } from '../../../stores/tenant.store';
import { todayInZoneWeb } from '@dosfilos/shared';
import { doc, getDoc } from 'firebase/firestore';
import { useCurrentMembership } from '../../../hooks/use-current-membership';
import { useCurrentPersonId } from '../../../hooks/use-current-person-id';
import { useAuth } from '../../../contexts/auth.context';
import { db } from '../../../lib/firebase';
import { attendanceApi, type AttendanceMatrixDto } from '../../../lib/api/attendance.api';
import { jornadasPendientes } from '../../../lib/attendance-pending';

// ─── El resumen del curso ────────────────────────────────────────────────────
//
// EL ATERRIZAJE DE LA SECCIÓN, no una pestaña más. Para saber cómo va un curso
// había que abrir tres —Tareas, Notas, Cronograma— y comparar a ojo.
//
// CADA NÚMERO LLEVA A DONDE SE ACTÚA. Es la condición de que esto siga
// mirándose: ya aprendimos que «un informe que obliga a anotar nombres para ir
// a buscarlos después se deja de mirar al tercer mes».
//
// LO QUE FALTA CARGAR VA PRIMERO, y no es una elección estética: medido en
// producción el 2026-09-26, CINCO de nueve secciones con alumnos no tenían una
// sola tarea publicada ni syllabus —noventa y una matrículas— y nadie lo veía,
// porque el único informe que lo decía estaba cerrado para el docente.
//
// NO HAY ASISTENCIA ACÁ. Hoy es «—» en toda la base; una tarjeta vacía enseña a
// ignorar la pantalla entera.

interface Props {
  sectionId: string;
  structure: SectionStructureDto | null;
  /** Lleva a una pestaña del propio curso. La acción ocurre donde se trabaja. */
  onGoToTab: (
    tab: 'modules' | 'syllabus' | 'cronograma' | 'asistencia' | 'asignaciones' | 'notas',
  ) => void;
  /** Con `graduation_audit`: sin «cómo rindió» (su escala numérica no es la
   *  del seminario, ver curso-tabs) ni la alarma de «ninguna tarea publicada»
   *  (ver course-summary.alerts). */
  importedRecord?: boolean;
}

/** El módulo que corresponde a hoy, y si vamos atrasados respecto del plan.
 *
 *  SIN CRONOGRAMA NO DICE NADA. Inventar un «vamos al día» sobre un curso sin
 *  fechas cargadas sería exactamente la clase de dato fabricado que este
 *  resumen existe para evitar. */
function dondeVamos(
  structure: SectionStructureDto | null,
  hoy: string,
): {
  actual?: string;
  atrasados: number;
  conFechas: boolean;
} {
  const conFecha = (structure?.modules ?? []).filter((m) => m.firstSessionDate);
  if (conFecha.length === 0) return { atrasados: 0, conFechas: false };
  const enCurso = conFecha.filter(
    (m) => m.firstSessionDate! <= hoy && (m.lastSessionDate ?? m.firstSessionDate!) >= hoy,
  );
  const pasados = conFecha.filter((m) => (m.lastSessionDate ?? m.firstSessionDate!) < hoy);
  const actual = enCurso[0]?.title ?? pasados[pasados.length - 1]?.title;
  return {
    ...(actual ? { actual } : {}),
    // Módulos cuya última clase ya pasó y que siguen sin publicar: el plan
    // avanzó y el material no.
    atrasados: pasados.filter((m) => !m.publishedAt).length,
    conFechas: true,
  };
}

/** Los alumnos que no entregaron NADA de lo vencido. Con nombre: es su curso, y
 *  una cifra sin nombres obliga a ir a buscarlos a otra pantalla. */
function enRiesgo(
  detalle: SectionWorkDetail | null,
): Array<{ id: string; nombre: string; n: number }> {
  if (!detalle) return [];
  // EL ID SOBREVIVE AL AGRUPADO. Dos alumnos homónimos son dos personas, y una
  // lista con el nombre por clave los fundiría en una sola fila.
  const porAlumno = new Map<string, { id: string; nombre: string; n: number }>();
  for (const i of detalle.missing as WorkItem[]) {
    const previo = porAlumno.get(i.studentId);
    if (previo) previo.n += 1;
    else porAlumno.set(i.studentId, { id: i.studentId, nombre: i.studentName, n: 1 });
  }
  return [...porAlumno.values()].sort((a, b) => b.n - a.n || a.nombre.localeCompare(b.nombre));
}

export function CourseSummary({
  sectionId,
  structure,
  onGoToTab,
  importedRecord = false,
}: Props): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const tenantTz = useTenantTimeZone();
  const [detalle, setDetalle] = useState<SectionWorkDetail | null>(null);
  const [tareas, setTareas] = useState<AssignmentSummaryDto[] | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // LA MISMA REGLA QUE LA PESTAÑA, importada y no copiada: si el contador dijera
  // cuatro y la pestaña mostrara tres, el indicador dejaría de creerse.
  const [asistencia, setAsistencia] = useState<AttendanceMatrixDto | null>(null);
  // «NO PUDE LEER» NO ES «NO HAY NADA PENDIENTE». Sin distinguirlos, un fallo
  // de permiso pintaba un cero tranquilizador: el indicador diría que está todo
  // al día justo cuando no sabe nada. Es la misma mentira silenciosa que un
  // promedio sin datos.
  const [asistenciaFallo, setAsistenciaFallo] = useState(false);

  // ¿LE TOCA ESTE RESUMEN? El servidor da el detalle de trabajo, las tareas y
  // el rendimiento a la gestión académica y a quien enseña en ESTA sección
  // (titular o ayudante). Un mentor —o un docente que abre el curso de al
  // lado— recibía tres rechazos en rojo; ahora ve un texto que explica de quién
  // es esta vista y adónde mirar. `null` = todavía no se sabe.
  const membership = useCurrentMembership();
  const myPersonId = useCurrentPersonId();
  const { isPlatformAdmin } = useAuth();
  const [puedeVer, setPuedeVer] = useState<boolean | null>(null);
  const [termId, setTermId] = useState<string | null>(null);
  useEffect(() => {
    if (!currentTenantId || !sectionId) return;
    let vigente = true;
    setPuedeVer(null);
    getDoc(doc(db, `tenants/${currentTenantId}/sections/${sectionId}`))
      .then((snap) => {
        if (!vigente) return;
        const d = snap.data() ?? {};
        const roles = membership?.roles ?? [];
        const gestion =
          isPlatformAdmin ||
          ['tenantAdmin', 'registrar', 'academicDean'].some((r) => roles.includes(r as never));
        const ensena =
          !!myPersonId &&
          (d['facultyId'] === myPersonId ||
            ((d['assistantIds'] as string[] | undefined) ?? []).includes(myPersonId));
        setTermId((d['termId'] as string | undefined) ?? null);
        setPuedeVer(gestion || ensena);
      })
      .catch((e) => {
        if (!vigente) return;
        setError(errorText(e));
        setPuedeVer(false);
      });
    return () => {
      vigente = false;
    };
  }, [currentTenantId, sectionId, membership, myPersonId, isPlatformAdmin]);

  // EL PERÍODO SALE DE LA SECCIÓN, no de la pantalla. La página de Curso no lo
  // conoce, y hacerlo bajar por toda la jerarquía sólo para esta consulta
  // pondría en cuatro sitios un dato que ya vive en un documento.

  useEffect(() => {
    if (!currentTenantId || !sectionId || puedeVer !== true) return;
    let vigente = true;
    // SIN RUIDO SI FALLA: la asistencia es un indicador más de esta pantalla, y
    // un error acá no debe tapar el resto del resumen.
    attendanceApi
      .getSectionAttendanceMatrix({ tenantId: currentTenantId, sectionId })
      .then((d) => {
        if (!vigente) return;
        setAsistencia(d);
        setAsistenciaFallo(false);
      })
      .catch(() => vigente && setAsistenciaFallo(true));
    return () => {
      vigente = false;
    };
  }, [currentTenantId, sectionId, puedeVer]);
  useEffect(() => {
    if (!currentTenantId || !sectionId || puedeVer !== true) return;
    let vigente = true;
    setCargando(true);
    setError(null);
    (async () => {
      if (!termId) throw new Error('section without term');
      // LAS TAREAS SE PREGUNTAN A LAS TAREAS. Antes se contaban desde la
      // estructura de módulos, y esa estructura describe lo que el alumno ve
      // DENTRO de un módulo: un curso cuyas tareas no están asignadas a
      // ninguno aparecía con cero. En producción le pasaba a Teología III, con
      // seis tareas publicadas, y la pantalla anunciaba en rojo que los alumnos
      // no tenían nada que entregar.
      const [d, a] = await Promise.all([
        programEnrollmentApi.getSectionWorkDetail({ tenantId: currentTenantId, termId, sectionId }),
        assignmentApi.list({ tenantId: currentTenantId, sectionId }),
      ]);
      return { d, items: a.items };
    })()
      .then(({ d, items }) => {
        if (!vigente) return;
        setDetalle(d);
        setTareas(items);
      })
      .catch((e) => {
        if (vigente) setError(errorText(e));
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });
    return () => {
      vigente = false;
    };
  }, [currentTenantId, sectionId, puedeVer, termId]);

  const publicadas = (tareas ?? []).filter((a) => a.publishedAt).length;
  const sinPublicar = (tareas ?? []).length - publicadas;
  const asistenciaPendiente = jornadasPendientes(asistencia, todayInZoneWeb(tenantTz));

  const modulosSinClases = (structure?.modules ?? []).filter((m) => !m.firstSessionDate).length;
  // EL DÍA LO DECIDE EL HUSO DEL CENTRO, no el del navegador. Las fechas del
  // cronograma son días de calendario; leídas desde otra zona, «qué módulo toca
  // hoy» se corre un día — y quien consulta desde fuera de Chile es el caso
  // normal para dirección, no el raro.
  const rumbo = dondeVamos(structure, todayInZoneWeb(tenantTz));
  const riesgo = enRiesgo(detalle);

  // `tareas === null` es «no se pudo leer», no «no hay». Un cero por una
  // lectura fallida y un cero real se ven idénticos, y ésta es justo la alarma
  // que esta pantalla existe para levantar.
  const alerts = loadAlerts({
    tasksRead: tareas !== null,
    published: publicadas,
    unpublished: sinPublicar,
    modulesWithoutSessions: modulosSinClases,
    importedRecord,
  });
  const sinNinguna = alerts.noAssignments;
  const faltaCargar = alerts.show;

  if (puedeVer === false) {
    return error ? (
      <Alert severity="error">{error}</Alert>
    ) : (
      <Alert severity="info">{t('courseSummary.notYours')}</Alert>
    );
  }

  if (puedeVer === null || cargando) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      {/* ── LO QUE FALTA CARGAR ── va primero porque es lo que nadie ve ── */}
      {faltaCargar && (
        <Alert
          severity={sinNinguna ? 'error' : 'warning'}
          icon={<WarningAmberRounded />}
          action={
            <Button color="inherit" size="small" onClick={() => onGoToTab('asignaciones')}>
              {t('courseSummary.goToAssignments')}
            </Button>
          }
        >
          <Stack spacing={0.5}>
            {sinNinguna && (
              <Typography variant="body2" fontWeight={700}>
                {t('courseSummary.noAssignments')}
              </Typography>
            )}
            {sinPublicar > 0 && (
              <Typography variant="body2">
                {t('courseSummary.unpublished', { count: sinPublicar })}
              </Typography>
            )}
            {modulosSinClases > 0 && (
              <Typography variant="body2">
                {t('courseSummary.modulesWithoutSessions', { count: modulosSinClases })}
              </Typography>
            )}
          </Stack>
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Tarjeta
          icono={<EventNoteRounded fontSize="small" />}
          titulo={t('courseSummary.whereWeAre')}
          valor={rumbo.conFechas ? (rumbo.actual ?? t('courseSummary.notStarted')) : '—'}
          detalle={
            !rumbo.conFechas
              ? t('courseSummary.noSchedule')
              : rumbo.atrasados > 0
                ? t('courseSummary.behind', { count: rumbo.atrasados })
                : t('courseSummary.onTrack')
          }
          alerta={rumbo.conFechas && rumbo.atrasados > 0}
          onIr={() => onGoToTab('cronograma')}
        />
        <Tarjeta
          icono={<PendingActionsRounded fontSize="small" />}
          titulo={t('courseSummary.weOwe')}
          valor={String(detalle?.awaitingGrading.length ?? 0)}
          detalle={t('courseSummary.weOweHelp')}
          alerta={(detalle?.awaitingGrading.length ?? 0) > 0}
          onIr={() => onGoToTab('asignaciones')}
        />
        {/* LA ASISTENCIA ES LA MITAD QUE CAMBIA LA CONDUCTA. Medido en
            producción: de 23 jornadas dictadas, 15 tienen asistencia — dos
            docentes al 100 % y dos casi en cero. Una pestaña la hace
            encontrable; esto es lo que además la RECUERDA, que es lo que
            faltaba. Sin este aviso, quien no se acuerda sigue sin acordarse. */}
        <Tarjeta
          icono={<HowToRegRounded fontSize="small" />}
          titulo={t('courseSummary.attendance')}
          valor={asistenciaFallo ? '—' : String(asistenciaPendiente.length)}
          detalle={
            asistenciaFallo
              ? t('courseSummary.attendanceUnavailable')
              : t('courseSummary.attendanceHelp')
          }
          alerta={!asistenciaFallo && asistenciaPendiente.length > 0}
          onIr={() => onGoToTab('asistencia')}
        />
        <Tarjeta
          icono={<AssignmentLateRounded fontSize="small" />}
          titulo={t('courseSummary.theyOwe')}
          valor={String(detalle?.missing.length ?? 0)}
          detalle={t('courseSummary.theyOweHelp', { count: riesgo.length })}
          alerta={(detalle?.missing.length ?? 0) > 0}
          onIr={() => onGoToTab('notas')}
        />
      </Stack>

      {/* ── CÓMO RINDIÓ ── abajo mientras el curso corre: no se acciona ── */}
      {!importedRecord && <CoursePerformance sectionId={sectionId} />}

      {/* ── QUIÉN ESTÁ EN RIESGO ── con nombre, que es lo accionable ── */}
      {riesgo.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {t('courseSummary.atRisk')}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            {t('courseSummary.atRiskHelp')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {riesgo.map((a) => (
              <Chip
                key={a.id}
                size="small"
                color={a.n >= 3 ? 'error' : 'warning'}
                variant="outlined"
                label={t('courseSummary.atRiskChip', { name: a.nombre, n: a.n })}
              />
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

function Tarjeta({
  icono,
  titulo,
  valor,
  detalle,
  alerta,
  onIr,
}: {
  icono: JSX.Element;
  titulo: string;
  valor: string;
  detalle: string;
  alerta: boolean;
  onIr: () => void;
}): JSX.Element {
  return (
    <Paper
      variant="outlined"
      onClick={onIr}
      sx={{
        p: 2,
        flex: 1,
        borderRadius: 2,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} color="text.secondary" mb={0.5}>
        {icono}
        <Typography variant="caption">{titulo}</Typography>
        <Box sx={{ flex: 1 }} />
        <ChevronRightRounded fontSize="small" />
      </Stack>
      <Typography variant="h6" fontWeight={700} color={alerta ? 'warning.main' : 'text.primary'}>
        {valor}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {detalle}
      </Typography>
    </Paper>
  );
}
